import React, { useContext, useCallback, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { assets } from '@yola/ws-sdk';
import { designSystem } from '@yola/ws-ui';
import elementSettings from 'src/js/modules/element-settings';
import ImagePreview from '../components/image-preview';
import useOnceWhen from 'src/js/modules/utils/custom-hooks/use-once-when';
import chunkIds from '../constants/chunk-ids';
import fallbackValues from '../constants/fallback-values';
import { imageFieldNames } from '../constants/field-names';
import settingIds from '../constants/setting-ids';
import AltControl from '../components/alt-control';
import LinkSettingsContainer from './link-settings-container';
import createSubmitActionsImageHandler from '../helpers/create-submit-actions-image-handler';
import ImageActionsContainer from './image-actions-container';

const { Box, Divider } = designSystem;
const {
  Context: ElementSettingsContext,
  constants: { stages },
  helpers: { wrapSharedDataWithId, getSharedDataById },
  hooks: { useMountChunk },
} = elementSettings;

const getDefaultValue = ({ mediaContainerElement, imageElement, cachedFieldsValues }) => {
  const src =
    cachedFieldsValues?.[imageFieldNames.IMAGE_SRC] ??
    (!mediaContainerElement.hasAttribute('placeholder') && imageElement?.getAttribute('src')) ??
    fallbackValues.IMAGE_SRC;
  const position =
    cachedFieldsValues?.[imageFieldNames.IMAGE_POSITION] ??
    mediaContainerElement?.getAttribute('content-position') ??
    undefined;
  const scale =
    cachedFieldsValues?.[imageFieldNames.IMAGE_SCALE] ??
    mediaContainerElement?.getAttribute('content-scale') ??
    undefined;
  const alt =
    cachedFieldsValues?.[imageFieldNames.IMAGE_ALT] ?? imageElement?.getAttribute('alt') ?? '';

  return {
    [imageFieldNames.IMAGE_SRC]: src,
    ...(position !== undefined && { [imageFieldNames.IMAGE_POSITION]: position }),
    ...(scale !== undefined && { [imageFieldNames.IMAGE_SCALE]: scale }),
    [imageFieldNames.IMAGE_ALT]: alt,
  };
};

function ImageSettingsContainer({ settingId, tabId, mediaContainerElement, cachedFieldsValues }) {
  const {
    tabs,
    form: { control, setValue },
    callbacks,
    sharedData,
  } = useContext(ElementSettingsContext);

  const imageElement = mediaContainerElement.querySelector('img');

  const chunkId = chunkIds.IMAGE;
  const isActiveTab = tabs.activeId === tabId;

  const imageElementRef = useRef(imageElement);
  const defaultValues = useMemo(
    () => getDefaultValue({ mediaContainerElement, imageElement, cachedFieldsValues }),
    [cachedFieldsValues, imageElement, mediaContainerElement]
  );
  const imageSettingsSharedData = getSharedDataById(chunkId, sharedData.internal);

  const rawImageSrc = imageElementRef.current?.getAttribute('src') || '';
  const imageSrc =
    rawImageSrc && !mediaContainerElement.hasAttribute('placeholder')
      ? assets.helpers.addBaseHref(rawImageSrc)
      : '';

  const handlePersistSharedData = useCallback(
    (data) => {
      sharedData.onPersist(
        wrapSharedDataWithId(chunkId, {
          ...getSharedDataById(chunkId, sharedData.internal),
          ...data,
        })
      );
    },
    [sharedData, chunkId]
  );

  const handleAltChange = useCallback(() => {
    // Add logic here
  }, []);

  const handleAltInputClick = useCallback(() => {
    callbacks.onInputClick({
      settingId: settingIds.THIS_MEDIA,
      controlId: 'alt-title',
    });
  }, [callbacks]);

  const handleImageCrop = useCallback(
    (changedAttributes) => {
      const dataToPersist = changedAttributes.reduce((acc, [attribute, value]) => {
        if (attribute === 'content-position') {
          acc[imageFieldNames.IMAGE_POSITION] = value;
        }

        if (attribute === 'content-scale') {
          acc[imageFieldNames.IMAGE_SCALE] = value;
        }

        return acc;
      }, {});

      handlePersistSharedData(dataToPersist);
    },
    [handlePersistSharedData]
  );

  const handleImageReplace = useCallback(
    ({ source }) => {
      const dataToPersist = {
        [imageFieldNames.IMAGE_SRC]: source,
        [imageFieldNames.IMAGE_POSITION]: fallbackValues.IMAGE_POSITION,
        [imageFieldNames.IMAGE_SCALE]: fallbackValues.IMAGE_SCALE,
      };

      handlePersistSharedData(dataToPersist);
    },
    [handlePersistSharedData]
  );

  const actionsHandler = useMemo(
    () => createSubmitActionsImageHandler({ mediaContainerElement }),
    [mediaContainerElement]
  );

  useOnceWhen(!!imageSettingsSharedData, () => {
    // Should be set in the next animation frame to avoid race condition with the setup of the form values
    requestAnimationFrame(() => {
      Object.entries(imageSettingsSharedData).forEach(([fieldName, value]) => {
        setValue(fieldName, value, { shouldDirty: true });
      });
    });
  });

  // Within image settings, multiple chunks are initialized (IMAGE, LINK, and maybe others in the future),
  // but we cannot allow each chunk to have its own actions handler as this would
  // cause conflicts since they all need to build the same markup structure.
  // Therefore, we use only one actions handler in the main chunk (IMAGE).
  // To do this, we need to collect field values from all related chunks.
  const { isChunkMounted } = useMountChunk(chunkId, {
    tabId,
    defaultValues,
    stageHandlers: [
      {
        stage: stages.SUBMIT_ACTIONS_GENERATION,
        handler: actionsHandler,
      },
    ],
    withUnmount: true,
  });

  if (!isChunkMounted) return null;

  return (
    <>
      <Box paddingLeft="spacing-m" paddingRight="spacing-m" paddingBottom="spacing-xs">
        <div className="ws-media-preview-with-actions">
          <ImagePreview
            url={imageSrc}
            mediaContainerElement={mediaContainerElement}
            previewKey={isActiveTab}
          />
          <div className="ws-media-preview-with-actions__actions">
            <ImageActionsContainer
              mediaContainerElement={mediaContainerElement}
              onCrop={handleImageCrop}
              onReplace={handleImageReplace}
            />
          </div>
        </div>
      </Box>
      <Divider appearance="light-grey" />
      <Box
        paddingTop="spacing-xs"
        paddingBottom="spacing-xs"
        paddingLeft="spacing-m"
        paddingRight="spacing-m"
      >
        <AltControl
          control={control}
          onChange={handleAltChange}
          onInputClick={handleAltInputClick}
        />
      </Box>
      <Divider appearance="light-grey" />
      <Box
        paddingTop="spacing-xs"
        paddingBottom="spacing-xs"
        paddingLeft="spacing-m"
        paddingRight="spacing-m"
      >
        <LinkSettingsContainer
          settingId={settingId}
          tabId={tabId}
          mediaContainerElement={mediaContainerElement}
          cachedFieldsValues={cachedFieldsValues}
        />
      </Box>
    </>
  );
}

ImageSettingsContainer.propTypes = {
  settingId: PropTypes.string.isRequired,
  tabId: PropTypes.string.isRequired,
  mediaContainerElement: PropTypes.oneOfType([PropTypes.instanceOf(Element), PropTypes.object])
    .isRequired,
  cachedFieldsValues: PropTypes.object,
};

export default ImageSettingsContainer;
